import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, CircleUser as UserCircle, PlusCircle, LogOut, Menu, X, MapPin, Camera, Users, DollarSign, Calendar, Gift } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Navbar() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const username = user?.email?.split('@')[0] || 'Guest';
  
  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/venues', icon: MapPin, label: 'Venues' },
    { path: '/vendors', icon: Camera, label: 'Vendors' },
    { path: '/budget', icon: DollarSign, label: 'Budget' },
    { path: '/day-of-planning', icon: Calendar, label: 'Day-Of Planning' },
    { path: '/registries', icon: Gift, label: 'Registries' },
    { path: '/create', icon: PlusCircle, label: 'Create Registry' },
    { path: '/profile', icon: UserCircle, label: `${username}` },
  ];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-xl border-b border-white/10 shadow-luxury">
      <div className="container-luxury">
        <div className="flex justify-between items-center h-16">
          {/* Brand */} {/* This div is for the logo */}
          <Link to="/dashboard" className="flex items-center group mr-6">
            <img src="/white-no-logo.png" alt="Giftyfy" className="h-20" style={{ mixBlendMode: 'screen' }} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                className={`group flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105 active:scale-95
                  ${location.pathname === path
                    ? 'text-white bg-luxury-gold-500/20 border border-luxury-gold-500/30 shadow-lg active:bg-luxury-gold-500/30'
                    : 'text-white/70 hover:text-white hover:bg-white/10 hover:shadow-md active:bg-luxury-gold-500/30'
                  }`}
              >
                <Icon className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-body">{label}</span>
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="group flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 hover:scale-105 active:scale-95 active:bg-red-500/20 transition-all duration-300"
            >
              <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
              <span className="font-body">Logout</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all duration-300"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`absolute top-full left-0 right-0 bg-black/80 backdrop-blur-xl border-b border-white/10 shadow-luxury md:hidden transition-all duration-300 ease-out transform origin-top ${
          isMenuOpen 
            ? 'scale-y-100 opacity-100' 
            : 'scale-y-0 opacity-0 pointer-events-none'
        }`}>
            <div className="py-2 space-y-2">
              {navItems.map(({ path, icon: Icon, label }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`group flex items-center space-x-3 px-6 py-4 text-sm font-medium transition-all duration-300 hover:scale-105 active:scale-95
                    ${location.pathname === path
                      ? 'text-white bg-luxury-gold-500/30 border-r-4 border-luxury-gold-500 shadow-lg active:bg-luxury-gold-500/40'
                      : 'text-white hover:text-white hover:bg-white/20 hover:shadow-md active:bg-luxury-gold-500/30'
                    }`}
                >
                  <Icon className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                  <span className="font-body">{label}</span>
                </Link>
              ))}
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="group flex items-center space-x-3 w-full px-6 py-4 text-sm font-medium text-white hover:text-white hover:bg-white/20 hover:scale-105 active:scale-95 active:bg-red-500/20 transition-all duration-300"
              >
                <LogOut className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-body">Logout</span>
              </button>
            </div>
        </div>
      </div>
    </nav>
  );
}